import { cache, sessionCache } from '~/common/useCache'
import { returnCode } from '~/constants/returnCode'
import { jwtDecode } from 'jwt-decode'
import qs from 'qs'

interface ApiResponse<T = any> {
  result: string
  code: string
  msg: string
  data?: T
}

interface TokenInfo {
  ACCESS_TOKEN: string
  REFRESH_TOKEN: string
}
const env = import.meta.env.VITE_ENV
const baseURL = import.meta.env.VITE_BASE_URL
const makeUrl = (endpoint: string): string => `${baseURL}${endpoint}`

// 토큰 갱신 상태 관리
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []
let refreshQueue: (() => void)[] = []
let tokenRefreshRequest: Promise<ApiResponse<TokenInfo>> | null = null

// 환경 체크 함수 추가
const isProduction = () => {
  return import.meta.env.VITE_ENV !== 'LOCAL'
}

// 토큰 관리 유틸리티
const tokenUtils = {
  getAccessToken: () => sessionCache.get('bus-a-token'),
  getRefreshToken: () => cache.get('bus-r-token'),
  setTokens: (accessToken: string, refreshToken?: string) => {
    sessionCache.set('bus-a-token', accessToken)
    if (refreshToken) cache.set('bus-r-token', refreshToken)
  },
  clearTokens: () => {
    sessionCache.clear()
    cache.clear()
  },
}

// 토큰 갱신 중 대기중인 요청 처리
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

// 토큰 갱신 API 호출 (동시 호출 시 진행 중인 요청을 공유)
export const requestTokenRefresh = async (): Promise<
  ApiResponse<TokenInfo>
> => {
  const refreshToken = tokenUtils.getRefreshToken()

  if (!refreshToken) {
    throw new Error('Refresh token not found')
  }

  if (!tokenRefreshRequest) {
    tokenRefreshRequest = $fetch<ApiResponse<TokenInfo>>(
      `${baseURL}/bus/api/common/commonRefreshToken`,
      {
        method: 'POST',
        headers: {
          // ofetch가 Accept 미지정 시 application/json을 넣는데, 운영 Apache는 이 값을 406으로 차단함
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: qs.stringify({ REFRESH_TOKEN: refreshToken }),
        ...(isProduction() && { credentials: 'include' }),
      },
    ).finally(() => {
      tokenRefreshRequest = null
    })
  }

  return tokenRefreshRequest
}

// 토큰 갱신 처리
const refreshAuthToken = async (): Promise<TokenInfo> => {
  const authStore = useAuthStore()
  const response = await requestTokenRefresh()

  if (response.code === returnCode.result_code && response.data) {
    const { ACCESS_TOKEN, REFRESH_TOKEN } = response.data
    tokenUtils.setTokens(ACCESS_TOKEN, REFRESH_TOKEN)

    authStore.setTokens(ACCESS_TOKEN, REFRESH_TOKEN)
    cache.set('bus-r-token', REFRESH_TOKEN)
    sessionCache.set('bus-a-token', ACCESS_TOKEN)
    return response.data
  }
  throw new Error('Token refresh failed')
}

// 토큰 만료 확인
const checkTokenExpiration = async (token: string): Promise<boolean> => {
  // let refresh = false
  try {
    const decoded = jwtDecode(token)
    // @ts-ignore
    const exp = decoded.exp
    const now = Date.now() / 1000
    if (exp) return exp < now
  } catch {
    return true
  }
}

// API 요청 설정 생성 (토큰 검증 및 갱신 포함)
const createFetchOptions = async (
  method: string,
  data?: unknown,
  customHeaders: Record<string, string> = {},
): Promise<Record<string, any>> => {
  const headers = {
    Accept: 'application/json, text/plain, */*', // 모든 컨텐츠 타입 허용
    'Content-Type':
      method === 'POST'
        ? 'application/x-www-form-urlencoded'
        : 'application/json',
    ...customHeaders,
  }

  const accessToken = tokenUtils.getAccessToken()
  const refreshToken = tokenUtils.getRefreshToken()
  const authStore = useAuthStore()

  // ⭐ accessToken이 없을 때도 isRefreshing 체크
  if (!accessToken) {
    if (refreshToken) {
      if (!isRefreshing) {
        isRefreshing = true
        try {
          const newTokens = await refreshAuthToken()
          const loginSuccess = await authStore.tokenLogin()

          if (!loginSuccess) {
            throw new Error('Token login failed')
          }

          //@ts-ignore
          headers['Authorization'] = `Bearer ${newTokens.ACCESS_TOKEN}`
          onTokenRefreshed(newTokens.ACCESS_TOKEN)
        } catch (error) {
          tokenUtils.clearTokens()
          authStore.logout()
          throw new Error('Token refresh failed')
        } finally {
          isRefreshing = false
        }
      } else {
        // ⭐ 이미 갱신 중이면 대기
        const newToken = await new Promise<string>((resolve) => {
          refreshSubscribers.push(resolve)
        })
        //@ts-ignore
        headers['Authorization'] = `Bearer ${newToken}`
      }
    }
    return {
      method,
      headers,
      ...(isProduction() && { credentials: 'include' }),
      ...(method === 'GET' ? { params: data } : { body: qs.stringify(data) }),
    }
  }

  // Request 전 토큰 만료 체크
  const isExpired = await checkTokenExpiration(accessToken)

  if (isExpired) {
    if (!isRefreshing) {
      isRefreshing = true
      try {
        const newTokens = await refreshAuthToken()
        // 토큰 갱신 후 로그인까지 완료되길 기다림
        const loginSuccess = await authStore.tokenLogin()

        if (!loginSuccess) {
          throw new Error('Token login failed')
        }

        //@ts-ignore
        headers['Authorization'] = `Bearer ${newTokens.ACCESS_TOKEN}`
        onTokenRefreshed(newTokens.ACCESS_TOKEN)
      } catch (error) {
        tokenUtils.clearTokens()
        authStore.logout()
        throw new Error('Token refresh failed')
      } finally {
        isRefreshing = false
      }
    } else {
      // 다른 요청이 토큰을 갱신하는 동안 대기
      const newToken = await new Promise<string>((resolve) => {
        refreshSubscribers.push(resolve)
      })
      //@ts-ignore
      headers['Authorization'] = `Bearer ${newToken}`
    }
  } else {
    //@ts-ignore
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  return {
    method,
    headers,
    ...(isProduction() && { credentials: 'include' }),
    ...(method === 'GET' ? { params: data } : { body: qs.stringify(data) }),
  }
}

// API 요청 실행
export const apiRequest = async <T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  data?: unknown,
  customHeaders: Record<string, string> = {},
): Promise<ApiResponse<T>> => {
  const authStore = useAuthStore()

  try {
    const url = makeUrl(endpoint)
    const options = await createFetchOptions(method, data, customHeaders)
    const response = await $fetch<ApiResponse<T>>(url, options)

    // 토큰 로그인 API에 대한 특별 처리
    if (endpoint.includes('/bus/api/login/loginTokenInfo')) {
      if (response.code !== returnCode.result_code) {
        // 토큰 로그인 실패 시 reject 페이지로 이동
        await navigateTo({
          path: '/login',
          query: {
            msg: response.msg || '토큰 로그인에 실패했습니다.',
          },
        })
        throw new Error('Token login failed')
      }
      return response
    }

    // 정상 응답
    if (response.code === returnCode.result_code || response.code === '200') {
      return response
    }

    // Response에서의 토큰 관련 에러 처리
    if (
      response.code === returnCode.decode_failed ||
      response.code === returnCode.token_not_found
    ) {
      console.log(`⛔${response.code}: 토큰 검증 실패`)
      tokenUtils.clearTokens()
      authStore.logout()
      throw new Error('Authentication failed')
    }

    // 개인정보 변경/삭제로 인한 토큰 만료 처리
    if (response.code === returnCode.token_invalid_change_info) {
      console.log(`⛔${response.code}: 개인정보 변경/삭제로 인한 만료`)
      tokenUtils.clearTokens()
      authStore.logout()
      throw new Error('User information changed or deleted')
    }

    if (response.code === returnCode.token_expired) {
      console.log(`⛔${response.code}: 토큰 유효기간 만료`)
      // Request에서 이미 처리했지만 혹시 모를 상황을 위한 재처리
      if (!isRefreshing) {
        isRefreshing = true
        try {
          const newTokens = await refreshAuthToken()
          onTokenRefreshed(newTokens.ACCESS_TOKEN)

          // 새로운 토큰으로 원래 요청 재시도
          // options.headers['Authorization'] = `Bearer ${newTokens.ACCESS_TOKEN}`
          // return await $fetch<ApiResponse<T>>(url, options)
          return await $fetch<ApiResponse<T>>(url, {
            ...options,
            credentials: 'include',
            headers: {
              ...options.headers,
              Authorization: `Bearer ${newTokens.ACCESS_TOKEN}`,
            },
          })
        } finally {
          isRefreshing = false
        }
      } else {
        return new Promise((resolve) => {
          refreshSubscribers.push((token: string) => {
            // options.headers['Authorization'] = `Bearer ${token}`
            // resolve($fetch<ApiResponse<T>>(url, options))
            resolve(
              $fetch<ApiResponse<T>>(url, {
                ...options,
                credentials: 'include',
                headers: {
                  ...options.headers,
                  Authorization: `Bearer ${token}`,
                },
              }),
            )
          })
        })
      }
    }

    console.log(`⛔${response.code}: API request failed`)
    throw new Error(response.msg || 'API request failed')
  } catch (error: any) {
    const status = error?.response?.status
    console.error(`❌ API Error:`, status, error)

    if (status === 401 && !isRefreshing) {
      return apiRequest<T>(endpoint, method, data, customHeaders)
    }

    throw error
  }
}

// API 메서드 래퍼
export const api = {
  get: <T>(endpoint: string, params?: any) =>
    apiRequest<T>(endpoint, 'GET', params),
  post: <T>(endpoint: string, data?: any) =>
    apiRequest<T>(endpoint, 'POST', data),
  put: <T>(endpoint: string, data?: any) =>
    apiRequest<T>(endpoint, 'PUT', data),
  del: <T>(endpoint: string, data?: any) =>
    apiRequest<T>(endpoint, 'DELETE', data),
}
